import React from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Grid,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Paper
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  Upgrade as UpgradeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { isPremiumUser, getPlanDisplayName } from '@utils/premium';
import PremiumBadge from '@shared/PremiumBadge';
import UpgradeButton from '@shared/UpgradeButton';
import subscriptionService from '@services/subscriptionService';
import LogoHPPlus from '@shared/LogoHPPlus';

interface PlanFeature {
  name: string;
  description?: string;
  available: boolean;
}

interface Plan {
  id: string;
  name: string;
  displayName: string;
  price: number;
  currency: string;
  billingCycle: 'monthly' | 'yearly';
  features: PlanFeature[];
  popular?: boolean;
  current?: boolean;
}

/**
 * Componentă PlanComparison - Pagină pentru comparare planuri Healthypaws+
 * 
 * Afișează un tabel comparativ între planurile disponibile:
 * - Basic (gratuit)
 * - Pro (plătit)
 * - Premium (plătit)
 */
const PlanComparison: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Planuri pentru utilizatori (clienți)
  const clientPlans: Plan[] = [
    {
      id: 'basic',
      name: 'Basic',
      displayName: 'Basic',
      price: 0,
      currency: 'RON',
      billingCycle: 'monthly',
      current: !isPremiumUser(user),
      features: [
        { name: 'Gestionare până la 2 animale', available: true },
        { name: 'Vizualizare istoric medical', available: true },
        { name: 'Programări online', available: true },
        { name: 'Notificări email', available: true },
        { name: 'Export date (limită)', available: false },
        { name: 'Prioritate booking', available: false },
        { name: 'Notificări SMS', available: false },
        { name: 'Reminder-uri automate', available: false },
        { name: 'Galerie foto nelimitată', available: false },
        { name: 'Suport prioritari', available: false }
      ]
    },
    {
      id: 'pro',
      name: 'Utilizator Pro',
      displayName: 'Healthypaws+ Pro',
      price: 9.99,
      currency: 'RON',
      billingCycle: 'monthly',
      popular: true,
      current: user?.activePlan?.name?.toLowerCase().includes('pro'),
      features: [
        { name: 'Gestionare până la 5 animale', available: true },
        { name: 'Istoric medical complet', available: true },
        { name: 'Programări online prioritare', available: true },
        { name: 'Notificări SMS și Email', available: true },
        { name: 'Reminder-uri automate', available: true },
        { name: 'Galerie foto animale', available: true },
        { name: 'Documente și certificate', available: true },
        { name: 'Export date extins', available: true },
        { name: 'Suport prioritari', available: false },
        { name: 'Telemedicină', available: false }
      ]
    },
    {
      id: 'premium',
      name: 'Utilizator Premium',
      displayName: 'Healthypaws+ Premium',
      price: 19.99,
      currency: 'RON',
      billingCycle: 'monthly',
      current: user?.activePlan?.name?.toLowerCase().includes('premium'),
      features: [
        { name: 'Gestionare animale nelimitată', available: true },
        { name: 'Istoric medical complet', available: true },
        { name: 'Programări online prioritare', available: true },
        { name: 'Notificări SMS și Email', available: true },
        { name: 'Reminder-uri automate', available: true },
        { name: 'Galerie foto nelimitată', available: true },
        { name: 'Documente și certificate', available: true },
        { name: 'Export date complet', available: true },
        { name: 'Suport prioritari 24/7', available: true },
        { name: 'Telemedicină', available: true }
      ]
    }
  ];
  
  const handleUpgrade = (planId: string) => {
    navigate('/messages', {
      state: {
        prefill:
          `Buna! Doresc activarea planului ${planId} pentru contul meu. Va rog sa ma contactati pentru finalizarea upgrade-ului.`,
      },
    });
  };
  
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>
          Alege Planul Perfect pentru Tine
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Toate planurile includ funcționalitățile de bază. Upgrade pentru mai multe beneficii!
        </Typography>
        {user?.activePlan && (
          <Chip
            label={`Plan curent: ${getPlanDisplayName(user)}`}
            color="primary"
            sx={{ mb: 2 }}
          />
        )}
      </Box>
      
      {/* Planuri */}
      <Grid container spacing={3}>
        {clientPlans.map((plan) => (
          <Grid size={{ xs: 12, md: 4 }} key={plan.id}>
            <Card
              elevation={plan.popular ? 8 : 2}
              sx={{
                position: 'relative',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: plan.popular ? '2px solid' : '1px solid',
                borderColor: plan.popular ? 'premium.main' : 'divider',
                borderRadius: 3,
                overflow: 'visible',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: plan.popular 
                    ? '0 12px 24px rgba(90, 128, 200, 0.3)' 
                    : '0 8px 16px rgba(0,0,0,0.1)'
                }
              }}
            >
              {/* Badge Popular */}
              {plan.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1
                  }}
                >
                  <PremiumBadge 
                    size="medium" 
                    variant="gradient" 
                    tooltip="Cel mai popular plan"
                  />
                </Box>
              )}
              
              {/* Badge Plan Curent */}
              {plan.current && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    zIndex: 1
                  }}
                >
                  <Chip
                    label="Plan curent"
                    color="success"
                    size="small"
                  />
                </Box>
              )}
              
              <CardContent sx={{ p: 3, pt: plan.popular ? 4 : 3 }}>
                {/* Header Plan */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    {plan.displayName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                    <Typography variant="h3" fontWeight={700} color="primary">
                      {plan.price === 0 ? 'Gratis' : `${plan.price} ${plan.currency}`}
                    </Typography>
                    {plan.price > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        /{plan.billingCycle === 'monthly' ? 'lună' : 'an'}
                      </Typography>
                    )}
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                {/* Features List */}
                <List dense sx={{ mb: 3 }}>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {feature.available ? (
                          <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CloseIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.name}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          color: feature.available ? 'text.primary' : 'text.disabled',
                          sx: {
                            textDecoration: feature.available ? 'none' : 'line-through',
                            opacity: feature.available ? 1 : 0.6
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                {/* Action Button */}
                <Box sx={{ mt: 'auto' }}>
                  {plan.current ? (
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled
                      sx={{ py: 1.5 }}
                    >
                      Plan Activ
                    </Button>
                  ) : plan.id === 'basic' ? (
                    <UpgradeButton
                      fullWidth
                      variant="outlined"
                      hideIfPremium={false}
                      customText="Upgrade acum"
                      sx={{ py: 1.5 }}
                    />
                  ) : (
                    <Button
                      fullWidth
                      variant={plan.popular ? 'contained' : 'outlined'}
                      onClick={() => handleUpgrade(plan.id)}
                      startIcon={plan.popular ? <LogoHPPlus variant="pawPlus" size={20} /> : <UpgradeIcon />}
                      sx={{
                        ...(plan.popular && {
                          background: 'linear-gradient(135deg, #5A80C8 0%, #4A70B8 50%, #3A60A8 100%)',
                          color: 'white',
                          fontWeight: 700,
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4A70B8 0%, #3A60A8 50%, #2A5088 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 6px 16px rgba(90,128,200,0.3)'
                          }
                        }),
                        py: 1.5
                      }}
                    >
                      {plan.price === 0 ? 'Începe acum' : 'Upgrade acum'}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Footer Note */}
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Toate planurile includ garanție de satisfacție de 30 de zile. 
          Poți anula oricând, fără întrebări.
        </Typography>
      </Box>
    </Container>
  );
};

export default PlanComparison;

